// The Honor Roll (Badges & Milestones) Component
// Dynamically rendered from Firestore system/badges with live database progress tracking
// Features 31 Event-Specific & Universal Badges (Attending 1, 5, 10 & Hosting 1, 5, 10 for all 5 event types)

import { getBadgesMasterList, calculateBadgeProgress, checkAndAutoAwardBadges } from '../services/badgeService';
import { getCurrentProfile } from '../services/authService';
import type { Mission, MaydayBeacon, BadgeCategory } from '../types';

let activeCategoryFilter: 'all' | BadgeCategory = 'all';

export function renderHonorRollView(missions: Mission[] = [], maydays: MaydayBeacon[] = []): string {
  const masterBadges = getBadgesMasterList();
  const profile = getCurrentProfile();

  // Automatically check and award badges in database if criteria are met
  if (profile) {
    checkAndAutoAwardBadges(profile, missions, maydays);
  }

  const earnedBadgeIds = profile?.badgesEarned || [];

  // Summary counts
  const totalBadges = masterBadges.length;
  const earnedCount = masterBadges.filter(b => earnedBadgeIds.includes(b.id) || calculateBadgeProgress(b, profile, missions, maydays).isEarned).length;
  const attendEarnedCount = masterBadges.filter(b => b.actionType === 'attend' && (earnedBadgeIds.includes(b.id) || calculateBadgeProgress(b, profile, missions, maydays).isEarned)).length;
  const hostEarnedCount = masterBadges.filter(b => b.actionType === 'host' && (earnedBadgeIds.includes(b.id) || calculateBadgeProgress(b, profile, missions, maydays).isEarned)).length;

  const categories: { key: 'all' | BadgeCategory; label: string; icon: string; count: number }[] = [
    { key: 'all', label: 'All Badges', icon: 'fa-layer-group', count: masterBadges.length },
    { key: 'Universal', label: 'Universal', icon: 'fa-bolt', count: masterBadges.filter(b => b.category === 'Universal').length },
    { key: 'Projects', label: 'Projects', icon: 'fa-hammer', count: masterBadges.filter(b => b.category === 'Projects').length },
    { key: 'Socials', label: 'Socials', icon: 'fa-burger', count: masterBadges.filter(b => b.category === 'Socials').length },
    { key: 'Volunteering', label: 'Volunteering', icon: 'fa-handshake-angle', count: masterBadges.filter(b => b.category === 'Volunteering').length },
    { key: 'Sports', label: 'Sports', icon: 'fa-basketball', count: masterBadges.filter(b => b.category === 'Sports').length },
    { key: 'Gaming', label: 'Gaming', icon: 'fa-gamepad', count: masterBadges.filter(b => b.category === 'Gaming').length }
  ];

  return `
    <div class="space-y-6 animate-fade-in font-sans">
      
      <!-- Honor Roll Hero Card -->
      <div class="card-workshop p-6 relative overflow-hidden">
        <div class="laser-line-cyan absolute top-0 left-0 right-0"></div>
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div class="space-y-1">
            <div class="flex items-center gap-2 text-xs font-bold text-cyan-400 uppercase tracking-wider mb-1" style="text-shadow: 0 0 8px rgba(0, 240, 255, 0.4);">
              <i class="fa-solid fa-trophy"></i> The Honor Roll
            </div>
            <h2 class="text-xl sm:text-2xl font-black text-slate-100">Dad Badges & Brotherhood Milestones</h2>
            <p class="text-xs sm:text-sm text-slate-400 max-w-2xl mt-1 leading-relaxed">
              Earn badges for showing up, lending a hand, and rallying neighbors. Badges track attendance and hosting milestones (1st, 5th, and 10th) across all 5 event types, plus the universal "First on the Scene" badge.
              <em class="text-slate-500 block sm:inline mt-1 sm:mt-0">Note: Badges reflect peer participation and camaraderie, not licensed vocational trade certifications.</em>
            </p>
          </div>

          <!-- Quick Metrics Grid -->
          <div class="flex items-center gap-3 flex-shrink-0">
            <div class="p-3 bg-[#0a0c13] rounded-xl border border-white/[0.08] text-center min-w-[75px]">
              <div class="text-xl font-black text-cyan-400" style="text-shadow: 0 0 8px rgba(0, 240, 255, 0.4);">${earnedCount} / ${totalBadges}</div>
              <div class="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Earned</div>
            </div>
            <div class="p-3 bg-[#0a0c13] rounded-xl border border-white/[0.08] text-center min-w-[75px]">
              <div class="text-xl font-black text-cyan-400" style="text-shadow: 0 0 8px rgba(0, 240, 255, 0.4);">${attendEarnedCount} / 15</div>
              <div class="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Attended</div>
            </div>
            <div class="p-3 bg-[#0a0c13] rounded-xl border border-white/[0.08] text-center min-w-[75px]">
              <div class="text-xl font-black text-cyan-400" style="text-shadow: 0 0 8px rgba(0, 240, 255, 0.4);">${hostEarnedCount} / 15</div>
              <div class="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Hosted</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Category Filter Tabs -->
      <div class="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
        ${categories.map(c => `
          <button data-badge-category="${c.key}" class="badge-filter-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap ${
            activeCategoryFilter === c.key
              ? 'bg-transparent text-cyan-300 border border-cyan-400 shadow-[0_0_10px_rgba(0,240,255,0.3)]'
              : 'bg-transparent text-slate-400 hover:text-white border border-white/[0.08] hover:border-white/20'
          }">
            <i class="fa-solid ${c.icon}"></i>
            <span>${c.label}</span>
            <span class="border border-white/10 px-1.5 rounded-full text-[10px]">${c.count}</span>
          </button>
        `).join('')}
      </div>

      <!-- Badges Cards Grid -->
      <div id="honor-roll-cards-grid" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        ${masterBadges.map(b => {
          const progressResult = calculateBadgeProgress(b, profile, missions, maydays);
          const isEarned = progressResult.isEarned || earnedBadgeIds.includes(b.id);
          const isVisible = activeCategoryFilter === 'all' || b.category === activeCategoryFilter;

          let rolePill = '';
          if (b.actionType === 'attend') {
            rolePill = `<span class="text-[10px] px-2 py-0.5 rounded border border-white/[0.12] text-slate-300 font-semibold"><i class="fa-solid fa-person-walking mr-1 text-cyan-400"></i> Attend (${b.threshold || 1})</span>`;
          } else if (b.actionType === 'host') {
            rolePill = `<span class="text-[10px] px-2 py-0.5 rounded border border-amber-400/40 text-amber-300 font-semibold"><i class="fa-solid fa-bullhorn mr-1 text-amber-400"></i> Host (${b.threshold || 1})</span>`;
          } else {
            rolePill = `<span class="text-[10px] px-2 py-0.5 rounded border border-pink-500/40 text-pink-400 font-semibold"><i class="fa-solid fa-bolt mr-1 text-pink-400"></i> Universal</span>`;
          }

          return `
            <div data-badge-cat="${b.category}" class="badge-card card-workshop p-5 flex items-start gap-4 transition-all ${
              !isVisible ? 'hidden' : ''
            } ${
              isEarned 
                ? 'border-cyan-400/50 shadow-[0_0_15px_rgba(0,240,255,0.12)]' 
                : 'opacity-65 border-white/[0.06] hover:opacity-100 hover:border-white/[0.15]'
            }">
              
              <div class="w-12 h-12 rounded-2xl flex items-center justify-center text-xl flex-shrink-0 ${
                isEarned 
                  ? 'bg-transparent border border-cyan-400 text-cyan-400 font-black shadow-[0_0_12px_rgba(0,240,255,0.35)]' 
                  : 'bg-transparent text-slate-600 border border-white/[0.08]'
              }">
                <i class="fa-solid ${b.icon}"></i>
              </div>

              <div class="space-y-1.5 flex-1 text-xs">
                <div class="flex items-center justify-between gap-1 flex-wrap">
                  <div class="flex items-center gap-1.5">
                    <span class="text-[10px] font-bold uppercase tracking-wider text-cyan-400/90">${b.category}</span>
                    ${rolePill}
                  </div>
                  ${isEarned ? `
                    <span class="text-[10px] font-bold px-2 py-0.5 rounded bg-transparent text-cyan-400 border border-cyan-400/60 shadow-[0_0_6px_rgba(0,240,255,0.25)]">
                      <i class="fa-solid fa-check"></i> Earned
                    </span>
                  ` : `
                    <span class="text-[10px] text-slate-500 font-medium">Locked</span>
                  `}
                </div>

                <h4 class="text-sm font-bold text-slate-100">${b.name}</h4>
                <p class="text-slate-400 leading-relaxed">${b.desc}</p>
                
                <div class="pt-1.5 space-y-1">
                  <div class="text-[11px] text-slate-400 font-semibold flex items-center justify-between">
                    <span class="flex items-center gap-1.5">
                      <i class="fa-solid fa-bars-progress text-[10px] text-cyan-400"></i>
                      <span>${progressResult.progressText}</span>
                    </span>
                    ${!isEarned ? `<span class="text-[10px] text-slate-500">${progressResult.percentage}%</span>` : ''}
                  </div>

                  <!-- Progress Bar Line -->
                  <div class="w-full bg-[#06070a] border border-white/[0.08] h-1.5 rounded-full overflow-hidden">
                    <div class="h-full bg-cyan-400 transition-all duration-500 ${isEarned ? 'shadow-[0_0_6px_rgba(0,240,255,0.6)]' : 'bg-slate-600'}" style="width: ${progressResult.percentage}%;"></div>
                  </div>
                </div>
              </div>

            </div>
          `;
        }).join('')}
      </div>

    </div>
  `;
}

export function bindHonorRollEvents(): void {
  document.querySelectorAll('.badge-filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const cat = (btn as HTMLElement).dataset.badgeCategory as 'all' | BadgeCategory;
      if (cat) {
        activeCategoryFilter = cat;
        // Update button states
        document.querySelectorAll('.badge-filter-btn').forEach(b => {
          b.className = 'badge-filter-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap bg-transparent text-slate-400 hover:text-white border border-white/[0.08] hover:border-white/20';
        });
        btn.className = 'badge-filter-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap bg-transparent text-cyan-300 border border-cyan-400 shadow-[0_0_10px_rgba(0,240,255,0.3)]';

        // Filter cards in place
        document.querySelectorAll('.badge-card').forEach(card => {
          const cardCat = (card as HTMLElement).dataset.badgeCat;
          if (cat === 'all' || cardCat === cat) {
            card.classList.remove('hidden');
          } else {
            card.classList.add('hidden');
          }
        });
      }
    });
  });
}
